import React, { useEffect, useState, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import slugify from "../utils/slugify";
import Breadcrumb from "../components/Breadcrumb";
import { useTranslation } from "react-i18next";

function OperaCities() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const fetchVenues = useCallback(async (retryCount = 0, maxRetries = 3) => {
    try {
      const res = await axios.get("/api/venues", {
        timeout: 15000,
      });

      // Ensure response data is an array
      setVenues(Array.isArray(res.data) ? res.data : []);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching venues:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        attempt: retryCount + 1,
      });
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        console.log(
          `Retrying fetch in ${delay}ms (attempt ${retryCount + 2}/${maxRetries + 1
          })`
        );
        setTimeout(() => fetchVenues(retryCount + 1, maxRetries), delay);
      } else {
        setError("Failed to load venues. Please try again.");
        setLoading(false);
      }
    }
  }, []);

  const cities = useMemo(() => {
    return Array.isArray(venues)
      ? venues.reduce((acc, venue) => {
        if (!venue?.cityNew?.name) return acc;
        const cityKey = venue?.cityNew?.name;
        if (!acc[cityKey]) acc[cityKey] = [];
        acc[cityKey].push(venue);
        return acc;
      }, {})
      : {};
  }, [venues]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    fetchVenues();
  };
  const { t } = useTranslation();
  return (
    <div className="bg-white">
      <div className="relative w-full h-[280px] lg:h-[33vw] overflow-hidden">
        <img
          src="/images/Teatro_alla_Scala.jpg"
          alt={t("explore")}
          onError={(e) => {
            e.target.src = "/images/default-pic.jpg";
          }}
          onLoad={() => setImageLoaded(true)}
          className="w-full h-full object-cover"
        />
        {imageLoaded && (
          <div className="absolute mt-20 md:mt-40 inset-0 flex flex-col items-center justify-center p-4 text-center">
            <h1
              className="text-2xl md:text-5xl lg:text-7xl mb-2 text-white font-cormorant-garamond font-medium"
              style={{
                textShadow: "1.5px 2px  rgba(0,0,0,0.9)",
              }}
            >
              {t("OperaCities.hero")} <br /> {t("OperaCities.hero2")}
            </h1>
          </div>
        )}
      </div>
      <Breadcrumb
        items={[
          { href: "/", label: "Home" },
          {
            href: "/opera-houses-and-festivals",
            label: "Destinations",
          },
        ]}
      />
      <main className="max-w-[1880px] mx-auto px-4 py-8 flex flex-col items-center">
        <div>
          <h1 className="text-4xl mb-2 text-left capitalize">
            {t("OperaCities.destinations")}
          </h1>
          <p className="text-black font-lato-light text-lg mb-8 text-left">
            {t("OperaCities.destinationsDescription")}
          </p>
          {loading ? (
            <p className="text-black text-center">{t("OperaCities.loading")}</p>
          ) : error ? (
            <div className="text-center">
              <p className="text-black">{error}</p>
              <button
                onClick={handleRetry}
                className="mt-4 bg-[#8C0000] text-white px-4 py-2 text-sm hover:bg-[#B20000] transition-colors"
              >
                {t("OperaCities.retry")}
              </button>
            </div>
          ) : Object.keys(cities).length === 0 ? (
            <p className="text-black text-center">
              {t("OperaCities.noCities")}
            </p>
          ) : (
            <div className="grid grid-col-1 md:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-6">
              {Object.entries(cities)
                .sort(([cityA], [cityB]) => cityA.localeCompare(cityB))
                .map(([city, cityVenues]) => {
                  const currentVenue = cityVenues[0];
                  const oneVenueInCity =
                    cityVenues.length === 1 && cityVenues[0]?.name;
                  const destination = oneVenueInCity
                    ? `/${slugify(cityVenues[0].name)}`
                    : `/opera-houses-and-festivals/${slugify(city)}`;
                  return (
                    <div key={city} className="mb-8">
                      <p className="text-xl text-black font-cinzel font-medium text-left mb-2">
                        {city}
                      </p>
                      <NavLink
                        to={destination}
                        className="block w-full h-[240px] 2xs:h-[280px] xs:h-[340px] sm:h-[380px] md:w-[345px] md:h-[213px] lg:w-[293px] lg:h-[182px] xl:w-[360px] xl:h-[223px] 3xl:w-[520px] 3xl:h-[321px] relative shadow-md transition-transform hover:scale-[1.01] mx-auto"
                      >
                        {oneVenueInCity && currentVenue.coverImage ? (
                          <>
                            <img
                              src={currentVenue.coverImage}
                              alt={currentVenue.name || city}
                              onError={(e) => {
                                e.target.src = "/images/default-pic.jpg";
                              }}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 p-2 w-full bg-gradient-mask">
                              <span className="text-white font-lato-normal-important text-lg uppercase">
                                {currentVenue.name}
                              </span>
                            </div>
                          </>
                        ) : !oneVenueInCity && currentVenue?.cityNew?.image ? (
                          <>
                            <img
                              src={currentVenue?.cityNew?.image}
                              alt={currentVenue.name || city}
                              onError={(e) => {
                                e.target.src = "/images/default-pic.jpg";
                              }}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 p-2 w-full bg-gradient-mask">
                              <span className="text-white font-lato-normal-important text-lg uppercase">
                                {`${currentVenue.name} & MORE`}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full bg-white relative">
                            <span className="absolute bottom-0 left-0 p-2 text-black font-lato-normal-important text-lg uppercase">
                              {`${currentVenue.name} & MORE`}
                            </span>
                          </div>
                        )}
                      </NavLink>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default OperaCities;
